import { UserQuota } from '../types';

/**
 * Centralized quota configuration to avoid duplication
 */

export type UsageType = 'totalCanvases' | 'threadStarters' | 'fileAttachments' | 'webSearches' | 'totalThreads' | 'monthlyChatMessages';

export type MaxKeyType = 'maxTotalCanvases' | 'maxThreadStarters' | 'maxFileAttachments' | 'maxWebSearches' | 'maxTotalThreads' | 'maxMonthlyChatMessages';

/**
 * Maps usage types to their corresponding max key types
 */
export const USAGE_TO_MAX_KEY_MAP: Record<UsageType, MaxKeyType> = {
  'totalCanvases': 'maxTotalCanvases',
  'threadStarters': 'maxThreadStarters',
  'fileAttachments': 'maxFileAttachments',
  'webSearches': 'maxWebSearches',
  'totalThreads': 'maxTotalThreads',
  'monthlyChatMessages': 'maxMonthlyChatMessages'
};

/**
 * Quota item configuration for UI components
 */
export interface QuotaItemConfig {
  key: UsageType;
  label: string;
  icon: string;
  description: string;
}

export const QUOTA_ITEMS_CONFIG: QuotaItemConfig[] = [
  {
    key: 'monthlyChatMessages',
    label: 'Monthly Chat Messages',
    icon: '💬',
    description: 'Total chat messages sent per month'
  },
  {
    key: 'totalCanvases',
    label: 'Total Canvases',
    icon: '📄',
    description: 'Total canvases created'
  },
  {
    key: 'threadStarters',
    label: 'Thread Starters',
    icon: '💡',
    description: 'Main idea notes created'
  },
  {
    key: 'fileAttachments',
    label: 'File Attachments',
    icon: '📎',
    description: 'Files uploaded'
  },
  {
    key: 'webSearches',
    label: 'Web Searches',
    icon: '🔍',
    description: 'AI web search queries'
  },
  {
    key: 'totalThreads',
    label: 'Total Threads',
    icon: '🌿',
    description: 'All notes created'
  }
];

/**
 * Helper function to get max key for a usage type
 */
export function getMaxKey(usageType: UsageType): MaxKeyType {
  return USAGE_TO_MAX_KEY_MAP[usageType];
}

/**
 * Helper function to check if a quota is unlimited
 */
export function isUnlimitedQuota(quota: UserQuota, usageType: UsageType): boolean {
  const maxKey = getMaxKey(usageType);
  return quota[maxKey] === -1;
}

/**
 * Helper function to get quota values
 */
export function getQuotaValues(quota: UserQuota, usageType: UsageType) {
  const used = quota[usageType];
  const maxKey = getMaxKey(usageType);
  const max = quota[maxKey];
  return { used, max, unlimited: max === -1 };
}
